<template>
  <div class="p-6 text-gray-900 bg-white rounded-lg text-left font-medium shadow-lg">
    <h1 class="font-semibold text-xl">
      <fa-icon
        :icon="['fas', 'upload']"
        size="lg"
        class="mr-1 text-blue-400"
      />
      {{ $t("Upload custom labels", "Upload custom labels") }}
      <span class="text-gray-500">
        / {{ fileName }}
      </span>
    </h1>
    <progress-bar
      v-if="isUploading"
      :all="selectedSheet.length - header"
      :completed="finishedCount"
      class="mt-4"
    />
    <div v-else>
      <div class="mt-4 flex">
        <div
          v-for="(sheetName, index) in Object.keys(workbook)"
          :key="index"
          class="rounded-t py-2 px-4 w-1/6 mr-2"
          :class="isSelected === index ? 'text-blue-400 bg-gray-100 cursor-default' : 'text-gray-600 bg-gray-200 cursor-pointer'"
          @click="selectSheet(index)"
        >
          <fa-icon
            v-if="isSelected === index"
            :icon="['fas', 'scrubber']"
            size="sm"
            class="mr-1 text-blue-400"
          />
          {{ sheetName }}
        </div>
      </div>
      <div class="-mt-2 p-4 bg-gray-100 w-full h-full font-normal flex overflow-auto overflow-x-auto">
        <div :style="headerSetterStyle">
          <fa-icon
            v-tooltip="$t('header.up', 'Move header upwards')"
            :icon="['fal', 'angle-up']"
            size="lg"
            class="text-gray-500 cursor-pointer hover:text-gray-700 block"
            @click="changeHeader('up')"
          />
          <fa-icon
            v-tooltip="$t('header.down', 'Move header downwards')"
            :icon="['fal', 'angle-down']"
            size="lg"
            class="text-gray-500 cursor-pointer hover:text-gray-700 block"
            @click="changeHeader('down')"
          />
        </div>
        <table>
          <tr class="w-full">
            <td
              v-for="n in selectedSheet[0].length"
              :key="n"
              class="mx-2"
            >
              <select
                :value="columnTypeValues[n-1]"
                class="p-2 mb-4 bg-gray-100 border-b-2 border-gray-500"
                @change="changeColumnType(n-1, $event.target.value)"
              >
                <option
                  v-for="(columnType, typeIndex) in columnTypes"
                  :key="typeIndex"
                  :value="typeIndex"
                >
                  {{ columnType }}
                </option>
              </select>
            </td>
          </tr>
          <tr
            v-for="(row, rowIndex) in selectedSheet"
            :key="rowIndex"
            class="border-b-2"
            :class="header === (rowIndex + 1) ? 'border-blue-400' : 'border-gray-200'"
          >
            <td
              v-for="(cell, columnIndex) in row"
              :key="columnIndex"
              class="p-2 max-w-lg overflow-hidden"
            >
              {{ cell }}
            </td>
          </tr>
        </table>
      </div>
      <div class="w-full mt-4 flex justify-end">
        <span class="py-2 font-normal">
          {{ selectedSheet.length - header }} {{ $t("Lines", "lines") }}
        </span>
        <z-button
          type="primary"
          :text="$t('Upload', 'Upload')"
          @click="upload"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCircle, faPlusCircle, faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons'
import { faUpload, faScrubber } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { actionNames } from '@/custom-labels/store'
import ZButton from '@/global/ui/Button'
import { mapState } from 'vuex'
import PQueue from 'p-queue'
import ProgressBar from '@/global/ui/ProgressBar'

library.add(faCircle, faPlusCircle, faAngleDown, faAngleUp, faUpload, faScrubber)
export default {
  name: 'UploadedLabels',
  components: {
    'fa-icon': FontAwesomeIcon,
    ZButton,
    ProgressBar
  },
  props: {
    fileName: { type: String, default: '' },
    workbook: { type: Object, default: () => {} }
  },
  data () {
    return {
      isUploading: false,
      finishedCount: 0,
      isSelected: 0,
      selectedSheet: [[], []],
      header: 1,
      columnTypeValues: [1, 2, 3, 4],
      variableColumns: {
        label: 0,
        synonym: 1,
        exclusion: 2,
        project: 3
      },
      labelCount: 1,
      synonymCount: 1,
      exclusionCount: 1,
      projectCount: 1
    }
  },
  computed: {
    ...mapState({
      projects: state => state.projects.projects
    }),
    columnTypes () {
      return [
        this.$t('Column type not used', 'Don\'t use'),
        this.$t('Column type label', 'Custom Label name'),
        this.$t('Column type synonym', 'Synonyms'),
        this.$t('Column type exclusion', 'Exclusions'),
        this.$t('Column type projects', 'Projects')
      ]
    },
    headerSetterStyle () {
      const h = this.header * 42 + 36
      return 'margin-top:' + h + 'px'
    }
  },
  async mounted () {
    this.selectSheet(0)
  },
  methods: {
    selectSheet (i) {
      this.isSelected = i
      this.selectedSheet = this.workbook[Object.keys(this.workbook)[i]]
      this.setColumnTypesForAllColumns()
    },
    setColumnTypesForAllColumns () {
      const newColumnTypes = [...this.columnTypeValues]
      for (let c = 0; c < (this.selectedSheet[0].length - this.columnTypeValues.length); c++) {
        newColumnTypes.push(0)
      }
      this.columnTypeValues = newColumnTypes
    },
    changeHeader (direction) {
      if (direction === 'up') {
        this.header--
      } else if (direction === 'down') {
        this.header++
      }
    },
    changeColumnType (index, newTypeId) {
      this.columnTypeValues = this.columnTypeValues.map((typeId, i) => {
        return index === i ? parseInt(newTypeId) : typeId
      })
      this.refreshVariables()
    },
    refreshVariables () {
      this.labelCount = 0
      this.synonymCount = 0
      this.exclusionCount = 0
      this.projectCount = 0
      this.variableColumns = {
        label: 0,
        synonym: 1,
        exclusion: 2,
        project: undefined
      }
      for (let c = 0; c < this.columnTypeValues.length; c++) {
        switch (this.columnTypeValues[c]) {
          case 1:
            this.variableColumns.label = c
            this.labelCount++
            break
          case 2:
            this.variableColumns.synonym = c
            this.synonymCount++
            break
          case 3:
            this.variableColumns.exclusion = c
            this.exclusionCount++
            break
          case 4:
            this.variableColumns.project = c
            this.projectCount++
            break
        }
      }
    },
    async upload () {
      const queue = new PQueue({ concurrency: 2 })
      if (this.labelCount !== 1 || this.synonymCount !== 1 || this.exclusionCount !== 1 || this.projectCount > 1) {
        this.$toast.error(this.$t('Incorrect column types', 'Please check if you have exactly one Custom Label, Synonyms and Exclusions column and maximum 1 Projects column.'))
        return
      }
      this.isUploading = true
      this.selectedSheet
        .slice(this.header)
        .forEach(row => queue.add(() =>
          this.$store.dispatch(actionNames.ADD_CUSTOM_LABEL, {
            name: row[this.variableColumns.label].trim(),
            synonyms: row[this.variableColumns.synonym] ? this.getSynonymExcludeArray(row[this.variableColumns.synonym]) : null,
            excludes: row[this.variableColumns.exclusion] ? this.getSynonymExcludeArray(row[this.variableColumns.exclusion]) : null,
            projects: this.getProjectIdsForRow(row)
          }).then(() => this.finishedCount++)
            .catch(() => this.$toast.error(`Could not upload custom label: ${row[this.variableColumns.label]}`))
        ))
      await queue.onEmpty()
      this.isUploading = false
      this.$emit('finished')
      this.$toast.success(this.$t('Custom labels uploaded', 'Custom labels uploaded'))
    },
    getSynonymExcludeArray (text) {
      return text.split(',').map(string => string.trim())
    },
    getProjectIdsForRow (row) {
      if (this.variableColumns.project === undefined || !row[this.variableColumns.project]) {
        return []
      }
      return row[this.variableColumns.project].split(',')
        .map(projectName => projectName.trim())
        .map(projectName => this.projects.find(p => p.name === projectName))
        .filter(project => !!project)
        .map(project => project.id)
    }
  }
}
</script>
